import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

/**
 * Estado que sobrevive a sair da tela e voltar.
 *
 * Fica no localStorage do navegador, sob a chave dada, e é lido uma vez, na
 * montagem. `restore` recebe o que estava gravado — pode ser de uma versão
 * antiga do sistema, ou nem ser um objeto — e devolve um valor válido, ou
 * `undefined` para começar de `initial`. É o que impede um registro velho de
 * quebrar a tela depois de uma mudança de formato.
 *
 * Navegador sem armazenamento (modo privado, cota cheia) não é erro: o estado
 * vale enquanto a tela está aberta, como sempre valeu.
 */
export function useStoredState<T>(
  key: string,
  initial: T,
  restore: (raw: unknown) => T | undefined,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => read(key, restore) ?? initial);

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Sem armazenamento, o estado vale só enquanto a tela está aberta.
    }
  }, [key, value]);

  return [value, setValue];
}

function read<T>(key: string, restore: (raw: unknown) => T | undefined): T | undefined {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? undefined : restore(JSON.parse(raw));
  } catch {
    return undefined;
  }
}
